import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Modal, Field, Check, inputCls } from "./AdminLayout";
import ImageUpload from "../components/ImageUpload";
import { adminCreatePromotion, adminUpdatePromotion, TYPE_META } from "../lib/promotions";

const uid = () => Math.random().toString(36).slice(2, 10);
const BASE = { title: "", description: "", prize: "", image: "", start_date: "", end_date: "", active: true, sponsor_name: "", sponsor_logo: "",
  sponsor_coupon: "", entry_frequency: "once", sms_keyword: "", lead_questions: [], categories: [], quiz_kind: "personality", questions: [], results: [], emails: {} };

export default function PromoBuilder({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...BASE, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Please add a title.");
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.keys(BASE).map((k) => [k, f[k]]).concat([["type", f.type]]));
      onSaved(f.id ? await adminUpdatePromotion(f.id, payload) : await adminCreatePromotion(payload));
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save."); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${f.id ? "Edit" : "New"} ${TYPE_META[f.type]?.label}`} onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Save Campaign">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" full><input data-testid="pb-title" value={f.title} onChange={set("title")} className={inputCls} /></Field>
        <Field label="Description" full><textarea data-testid="pb-description" rows={2} value={f.description} onChange={set("description")} className={inputCls} /></Field>
        <Field label="Prize / payoff"><input data-testid="pb-prize" value={f.prize} onChange={set("prize")} className={inputCls} /></Field>
        <Field label="Ends (optional)"><input data-testid="pb-end" type="date" value={f.end_date} onChange={set("end_date")} className={inputCls} /></Field>
        <Field label="Image" full><ImageUpload value={f.image} onChange={(url) => setF((x) => ({ ...x, image: url }))} testid="pb-image" /></Field>
        <Field label="Sponsor"><input data-testid="pb-sponsor" value={f.sponsor_name} onChange={set("sponsor_name")} className={inputCls} /></Field>
        {f.type !== "ballot" && <Field label="Text keyword (texting not connected yet)"><input data-testid="pb-keyword" value={f.sms_keyword} onChange={set("sms_keyword")} placeholder="e.g. BEACH" className={`${inputCls} uppercase`} /></Field>}
        <Field label="Sponsor thank-you offer (shown after entering)" full><input data-testid="pb-coupon" value={f.sponsor_coupon} onChange={set("sponsor_coupon")} className={inputCls} /></Field>
        {f.type === "sweepstakes" && (
          <Field label="Entry limit"><select data-testid="pb-frequency" value={f.entry_frequency} onChange={set("entry_frequency")} className={inputCls}><option value="once">One entry per person</option><option value="daily">One entry per day</option></select></Field>
        )}
        <Check label="Live on the site" checked={f.active} onChange={() => setF({ ...f, active: !f.active })} testid="pb-active" />
      </div>
      {f.type === "ballot" && <BallotEditor f={f} setF={setF} />}
      {f.type === "quiz" && <QuizEditor f={f} setF={setF} />}
      <LeadEditor f={f} setF={setF} />
    </Modal>
  );
}

const Section = ({ title, onAdd, addLabel, children, testid }) => (
  <div className="rounded-xl border border-slate-200 p-4" data-testid={testid}>
    <div className="mb-3 flex items-center justify-between"><p className="text-sm font-bold text-[var(--navy)]">{title}</p>
      <button type="button" onClick={onAdd} data-testid={`${testid}-add`} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"><Plus size={13} /> {addLabel}</button></div>
    <div className="space-y-3">{children}</div>
  </div>
);
const Del = ({ onClick }) => <button type="button" onClick={onClick} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>;

function BallotEditor({ f, setF }) {
  const cats = f.categories;
  const upd = (i, patch) => setF({ ...f, categories: cats.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <Section title="Categories & nominees" addLabel="Category" testid="pb-categories" onAdd={() => setF({ ...f, categories: [...cats, { id: uid(), name: "", nominees: [] }] })}>
      {cats.map((c, i) => (
        <div key={c.id} className="rounded-lg bg-slate-50 p-3">
          <div className="flex gap-2"><input value={c.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Best Burger" className={inputCls} /><Del onClick={() => setF({ ...f, categories: cats.filter((_, j) => j !== i) })} /></div>
          {c.nominees.map((n, k) => (
            <div key={n.id} className="mt-2 flex gap-2 pl-3">
              <input value={n.name} onChange={(e) => upd(i, { nominees: c.nominees.map((x, m) => (m === k ? { ...x, name: e.target.value } : x)) })} placeholder="Nominee" className={inputCls} />
              <input value={n.sms_keyword || ""} onChange={(e) => upd(i, { nominees: c.nominees.map((x, m) => (m === k ? { ...x, sms_keyword: e.target.value.toUpperCase() } : x)) })} placeholder="KEYWORD" className={`${inputCls} w-32 uppercase`} />
              <Del onClick={() => upd(i, { nominees: c.nominees.filter((_, m) => m !== k) })} />
            </div>
          ))}
          <button type="button" onClick={() => upd(i, { nominees: [...c.nominees, { id: uid(), name: "", sms_keyword: "" }] })} className="mt-2 pl-3 text-xs font-semibold text-[var(--gold)]">+ Nominee</button>
        </div>
      ))}
    </Section>
  );
}

function QuizEditor({ f, setF }) {
  const qs = f.questions;
  const personality = f.quiz_kind !== "trivia";
  const updQ = (i, patch) => setF({ ...f, questions: qs.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  const updA = (i, k, patch) => updQ(i, { answers: qs[i].answers.map((a, m) => (m === k ? { ...a, ...patch } : a)) });
  return (
    <div className="space-y-4">
      <Field label="Quiz style"><select data-testid="pb-quiz-kind" value={f.quiz_kind} onChange={(e) => setF({ ...f, quiz_kind: e.target.value })} className={inputCls}><option value="personality">Personality (which ___ are you?)</option><option value="trivia">Trivia (scored)</option></select></Field>
      {personality && (
        <Section title="Possible results" addLabel="Result" testid="pb-results" onAdd={() => setF({ ...f, results: [...f.results, { id: uid(), title: "", description: "" }] })}>
          {f.results.map((r, i) => (
            <div key={r.id} className="flex gap-2">
              <input value={r.title} onChange={(e) => setF({ ...f, results: f.results.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} placeholder="Result title" className={inputCls} />
              <input value={r.description} onChange={(e) => setF({ ...f, results: f.results.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} placeholder="Description" className={inputCls} />
              <Del onClick={() => setF({ ...f, results: f.results.filter((_, j) => j !== i) })} />
            </div>
          ))}
        </Section>
      )}
      <Section title="Questions" addLabel="Question" testid="pb-questions" onAdd={() => setF({ ...f, questions: [...qs, { id: uid(), text: "", answers: [] }] })}>
        {qs.map((q, i) => (
          <div key={q.id} className="rounded-lg bg-slate-50 p-3">
            <div className="flex gap-2"><input value={q.text} onChange={(e) => updQ(i, { text: e.target.value })} placeholder="Question" className={inputCls} /><Del onClick={() => setF({ ...f, questions: qs.filter((_, j) => j !== i) })} /></div>
            {q.answers.map((a, k) => (
              <div key={a.id} className="mt-2 flex items-center gap-2 pl-3">
                <input value={a.text} onChange={(e) => updA(i, k, { text: e.target.value })} placeholder="Answer" className={inputCls} />
                {personality ? (
                  <select value={a.result_id || ""} onChange={(e) => updA(i, k, { result_id: e.target.value })} className={`${inputCls} w-40`}><option value="">Points to…</option>{f.results.map((r) => <option key={r.id} value={r.id}>{r.title || "Untitled"}</option>)}</select>
                ) : (
                  <label className="flex items-center gap-1 whitespace-nowrap text-xs"><input type="checkbox" checked={!!a.correct} onChange={() => updA(i, k, { correct: !a.correct })} /> Correct</label>
                )}
                <Del onClick={() => updQ(i, { answers: q.answers.filter((_, m) => m !== k) })} />
              </div>
            ))}
            <button type="button" onClick={() => updQ(i, { answers: [...q.answers, { id: uid(), text: "" }] })} className="mt-2 pl-3 text-xs font-semibold text-[var(--gold)]">+ Answer</button>
          </div>
        ))}
      </Section>
    </div>
  );
}

function LeadEditor({ f, setF }) {
  const lq = f.lead_questions;
  const upd = (i, patch) => setF({ ...f, lead_questions: lq.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  return (
    <Section title="Sponsor lead questions (added to the entry form)" addLabel="Question" testid="pb-leads" onAdd={() => setF({ ...f, lead_questions: [...lq, { id: uid(), label: "", kind: "choice", options: ["Yes", "No"], required: false }] })}>
      {lq.map((q, i) => (
        <div key={q.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-3">
          <input value={q.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Are you planning a kitchen remodel this year?" className={`${inputCls} min-w-[220px] flex-1`} />
          <select value={q.kind} onChange={(e) => upd(i, { kind: e.target.value })} className={`${inputCls} w-32`}><option value="choice">Choices</option><option value="text">Free text</option></select>
          {q.kind === "choice" && <input value={q.options.join(", ")} onChange={(e) => upd(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Yes, No, Maybe" className={`${inputCls} w-40`} />}
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={q.required} onChange={() => upd(i, { required: !q.required })} /> Required</label>
          <Del onClick={() => setF({ ...f, lead_questions: lq.filter((_, j) => j !== i) })} />
        </div>
      ))}
    </Section>
  );
}
